import { useLazy } from '@app/hooks/useLazy.js';
import { useAppSelector } from '@shared/hooks/useAppSelector.js';
import { useMemo, useSyncExternalStore } from 'react';
import type { Messages } from '@/translations/messagesInterface.js';
import { getMessages, subscribeMessages } from './messagesStore.js';

export function useLocalMessages<T>(
  factory: (language: string) => Promise<{ default: T }>,
): T | undefined {
  const language = useAppSelector((state) => state.l10n.language);

  // `factory` must be module-scope stable; an inline one re-imports every render.
  const f = useMemo(
    () => factory.bind(undefined, language),
    [factory, language],
  );

  return useLazy(f);
}

export function useMessages(): Messages | undefined {
  return useSyncExternalStore(subscribeMessages, getMessages, getMessages);
}

export function getMessageByKey(m: Messages | undefined, key: string): unknown {
  if (m === undefined) {
    return;
  }

  const path = key.split('.');

  let cur = m as unknown;

  for (const item of path) {
    if (cur instanceof Object) {
      cur = (cur as Record<string, unknown>)[item];
    } else {
      return '…';
    }
  }

  return cur;
}
